import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { SignUpRequestModel } from "features/login/data/model/requestModel/SignUpRequestModel";
import { AuthenticationInjection } from "features/login/di/AuthenticationInjection";
import { AuthenticationRequestEntity } from "features/login/domain/entities/AuthenticationRequestEntity";
import { AuthenticationResponseEntity } from "features/login/domain/entities/AuthenticationResponseEntity";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";

class LoginInput implements Disposable {
  public login = new BehaviorSubject<AuthenticationRequestEntity>({});
  public loading = new BehaviorSubject<boolean>(false);
  public signUp = new BehaviorSubject<SignUpRequestModel>({});

  [Symbol.dispose](): void {
    this.login.complete();
    this.loading.complete();
    this.signUp.complete();
  }
}

class LoginOutput implements Disposable {
  public onLoginSuccess = new BehaviorSubject<AuthenticationResponseEntity>({});
  public onSignUpSuccess = new BehaviorSubject<string>("");
  public onError = new BehaviorSubject<Error>({});

  [Symbol.dispose](): void {
    this.onLoginSuccess.complete();
    this.onError.complete();
    this.onSignUpSuccess.complete();
  }
}

@injectable()
export class LoginViewModel extends BaseViewModel<LoginInput, LoginOutput> {
  constructor() {
    super(new LoginInput(), new LoginOutput());
  }

  private authenUseCase = AuthenticationInjection.getAuthenticationUseCase();

  protected override transform(): void {
    this.input.login
      .subscribe(async (body) => {
        if (isEmpty(body)) {
          return;
        } else {
          try {
            this.input.loading.next(true);
            let isSavedForNextLogin = body?.isSavedForNextLogin;
            const { data } = await this.authenUseCase?.login(body);
            if (data) {
              let finalData = data;
              new AuthenticationResponseEntity().saveAuthenToken(
                isSavedForNextLogin,
                finalData,
              );
              productCache?.saveCache({
                key: "user",
                product: finalData,
                needExpired: false,
              });
              this.output.onLoginSuccess.next(finalData);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
        }
      })
      .add(this.subscriptions);

    this.input.signUp
      .subscribe(async (body) => {
        if (isEmpty(body)) {
          return;
        } else {
          try {
            this.input.loading.next(true);
            const { data, message } = await this.authenUseCase?.signUp(body);
            if (data) {
              this.output.onSignUpSuccess.next(message);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
        }
      })
      .add(this.subscriptions);
  }
}
